import * as React from "react";
import { useState } from "react";
import { Download, Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Modal } from "@multiagentbook/ui";

interface ConvinceYourBossProps {
  className?: string;
}

const templateContent = `Request: Designing Multi-Agent Systems Book

To: [Manager]
From: [Your Name]
Re: Technical resource for AI agent development ($49-149)

What It Is

A technical book on building production AI agent systems. 15 chapters covering agent patterns, evaluation, optimization, and deployment. It guides the reader through implementing a complete Python library (picoagents) from scratch, providing insights on architectural trade-offs and design decisions.

Author: Victor Dibia, a core contributor to AutoGen (50K+ GitHub stars) and a researcher at Microsoft Research.

What I'll Learn

- Multi-Agent Fundamentals - Core concepts, design patterns, and UX principles for agent applications
- Build from Scratch - Implement agents, workflows, and orchestration via picoagents library
- Evaluation & Optimization - Trajectory-based metrics, LLM judges, 10 common failure modes
- Distributed Protocols - MCP integration, A2A for cross-org collaboration
- Real-World Applications - Complete implementations for data analysis and software engineering

Why This Over Tutorials

- Builds from first principles: Ch 4 (agent execution loop), Ch 5 (computer use agents), Ch 6 (workflows as computational graphs), Ch 7 (autonomous orchestration)
- Covers deployment: Ch 3 (UX principles), Ch 8 (web integration with SSE/WebSockets), Ch 12 (MCP/A2A protocols), Ch 13 (responsible AI)
- Real-world applications with code: Ch 14 (structured data analysis workflow), Ch 15 (SWE agent - the pattern behind Cursor, Claude Code, Copilot)
- Companion repo includes examples for Microsoft Agent Framework, Google ADK, LangGraph

Cost

- Essential ($49) - Digital book, lifetime updates
- Professional ($149) - Essential + sample apps, early access

Request

Requesting approval for [Essential/Professional] at $[49/149].

Time investment: ~15 hours. Applicable immediately to [project name].

Links:
- Purchase: buy.multiagentbook.com
- Book info: multiagentbook.com
- Code samples: github.com/victordibia/designing-multiagent-systems`;

/**
 * A compact single-line component that opens a modal with copyable template.
 */
export const ConvinceYourBoss: React.FC<ConvinceYourBossProps> = ({
  className = "",
}) => {
  const { t } = useTranslation("home");
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(templateContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <p className={`text-xs text-secondary/70 ${className}`}>
        {t("convinceYourBoss.prompt")}{" "}
        <button
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-1 text-accent hover:underline transition-colors"
        >
          {t("convinceYourBoss.downloadTemplate")}
        </button>
      </p>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t("convinceYourBoss.modalTitle")}
        size="lg"
      >
        <div className="p-4">
          <p className="text-sm text-secondary mb-4">
            {t("convinceYourBoss.instructions")}
          </p>

          <div className="bg-tertiary rounded-lg p-4 mb-4 max-h-[50vh] overflow-y-auto">
            <pre className="text-sm text-primary whitespace-pre-wrap font-sans">
              {templateContent}
            </pre>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleCopy}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-accent text-white rounded-md hover:bg-accent/90 transition-colors text-sm font-medium"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  {t("convinceYourBoss.copied")}
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  {t("convinceYourBoss.copyToClipboard")}
                </>
              )}
            </button>
            <a
              href="/convince-your-boss.pdf"
              download="convince-your-boss-multiagent-book.pdf"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-secondary text-primary rounded-md hover:bg-secondary transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              {t("convinceYourBoss.downloadPdf")}
            </a>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ConvinceYourBoss;
